import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, BarChart3, Briefcase, FileText, Target, TrendingUp, UserCheck, Users, Wallet, type LucideIcon } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { apiJson } from "@/lib/api";

export interface SalesKpi {
  label: string;
  value: string;
  sub: string;
  icon: LucideIcon;
}

interface Deal { deal: string; montant_eur: number; stage: string; proba: number; cloture_prevue?: string | null }
interface Mission { client: string; realise_eur: number; reste_a_facturer_eur: number }
interface CfoContext {
  treasury: { monthly_revenue: { month: string; realise: number }[] };
  portage_itg: { ca_realise_eur: number; reste_a_facturer_eur: number; missions: Mission[] };
  devis_en_cours: { devis_envoyes: Deal[]; total_eur: number; total_pondere_eur: number; date_instantane: string };
  pipeline_commercial: { deals: Deal[]; total_eur: number; total_pondere_eur: number };
  crm: { total_contacts: number; qualified: number; clients: number };
}

const COLORS = ["hsl(38, 90%, 55%)", "hsl(200, 70%, 50%)", "hsl(150, 60%, 45%)", "hsl(280, 60%, 55%)"];
const eur = (v: number) => `${Math.round(v).toLocaleString("fr-FR")} €`;
const tooltipStyle = { backgroundColor: "hsl(222,28%,13%)", border: "1px solid hsl(222,20%,20%)", borderRadius: 8, color: "hsl(220,20%,92%)" };

const alertStyle = (type: string) =>
  type === "danger" ? "border-destructive/40 bg-destructive/10 text-destructive"
  : type === "warning" ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-400"
  : "border-blue-500/40 bg-blue-500/10 text-blue-400";

export function SalesOverviewTab({ onKpis }: { onKpis?: (k: { label: string; value: string }[]) => void }) {
  const [ctx, setCtx] = useState<CfoContext | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiJson<CfoContext>("/api/cfo/context").then(setCtx).catch(() => setError(true));
  }, []);

  useEffect(() => {
    if (!ctx || !onKpis) return;
    onKpis([
      { label: "CA réalisé", value: eur(ctx.portage_itg.ca_realise_eur) },
      { label: "Reste à facturer", value: eur(ctx.portage_itg.reste_a_facturer_eur) },
      { label: "Devis envoyés", value: eur(ctx.devis_en_cours.total_eur) },
      { label: "Business potentiel pondéré", value: eur(ctx.pipeline_commercial.total_pondere_eur) },
    ]);
  }, [ctx, onKpis]);

  if (error) return <p className="text-sm text-destructive">Données commerciales momentanément indisponibles.</p>;
  if (!ctx) return <p className="text-sm text-muted-foreground">Chargement des chiffres…</p>;

  const { portage_itg: p, devis_en_cours: d, pipeline_commercial: pl, crm } = ctx;

  const byClient: Record<string, number> = {};
  p.missions.forEach((m) => { byClient[m.client] = (byClient[m.client] || 0) + m.realise_eur; });
  const clients = Object.entries(byClient).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const months = ctx.treasury.monthly_revenue.map((m) => ({ month: m.month, realise: m.realise }));

  const funnel = [
    { etape: "CA réalisé", ferme: p.ca_realise_eur },
    { etape: "Signé, à facturer", ferme: p.reste_a_facturer_eur },
    { etape: "Devis envoyés", potentiel: d.total_eur, pondere: d.total_pondere_eur },
    { etape: "Business potentiel", potentiel: pl.total_eur, pondere: pl.total_pondere_eur },
  ];
  const isAtelier = (x: Deal) => /atelier/i.test(x.deal);
  const ateliers = [...d.devis_envoyes, ...pl.deals].filter(isAtelier);
  const atelTotal = ateliers.reduce((a, x) => a + x.montant_eur, 0);
  const atelPondere = ateliers.reduce((a, x) => a + x.montant_eur * x.proba, 0);
  const potentiel = [
    ...d.devis_envoyes.map((x) => ({ ...x, nature: "Devis envoyé" })),
    ...pl.deals.map((x) => ({ ...x, nature: "Business potentiel" })),
  ].sort((a, b) => b.montant_eur - a.montant_eur);

  const kpis: SalesKpi[] = [
    { label: "CA réalisé", value: eur(p.ca_realise_eur), sub: "portage ITG, 3 clients", icon: Wallet },
    { label: "Reste à facturer", value: eur(p.reste_a_facturer_eur), sub: "contrat signé", icon: FileText },
    { label: "Devis envoyés", value: eur(d.total_eur), sub: `pondéré ${eur(d.total_pondere_eur)}`, icon: Briefcase },
    { label: "Business potentiel", value: eur(pl.total_eur), sub: `pondéré ${eur(pl.total_pondere_eur)}`, icon: TrendingUp },
    { label: "Clients actifs", value: String(clients.length), sub: "en portage ITG", icon: Users },
    { label: "Contacts CRM", value: String(crm.total_contacts), sub: `${crm.qualified} qualifiés`, icon: Target },
  ];

  const top = clients[0];
  const alerts = [
    { type: "info", message: `${eur(p.reste_a_facturer_eur)} restent à facturer (Pâte à Pizza, développement sur 1 an depuis le 01/10/2026).` },
    ...(top && p.ca_realise_eur > 0 ? [{ type: "warning", message: `${top.name} représente ${Math.round((top.value / p.ca_realise_eur) * 100)} % du CA réalisé : forte concentration client.` }] : []),
    { type: "info", message: "Financements : aucune aide n'est encore déposée ; le catalogue est en cours de vérification (onglet Financement)." },
    { type: "info", message: `Devis et pipeline : instantané HubSpot du ${new Date(d.date_instantane).toLocaleDateString("fr-FR")} (synchronisation automatique à rétablir).` },
  ];

  const actions = [...d.devis_envoyes].sort((a, b) => b.montant_eur * b.proba - a.montant_eur * a.proba).slice(0, 4);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="bg-card border-border">
            <CardContent className="p-4">
              <kpi.icon className="h-4 w-4 text-primary mb-2" />
              <p className="text-xl font-bold text-foreground">{kpi.value}</p>
              <p className="text-[11px] text-muted-foreground">{kpi.label}</p>
              <p className="text-[10px] text-muted-foreground/70">{kpi.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" />CA réalisé par mois (2026)</CardTitle>
            <CardDescription>Facturé en portage ITG</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={months}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(222,20%,20%)" />
                <XAxis dataKey="month" stroke="hsl(220,15%,55%)" fontSize={12} />
                <YAxis stroke="hsl(220,15%,55%)" fontSize={12} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => eur(v)} />
                <Bar dataKey="realise" fill="hsl(38,90%,55%)" radius={[4, 4, 0, 0]} name="CA réalisé" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" />Répartition du CA par client</CardTitle>
            <CardDescription>Total {eur(p.ca_realise_eur)}</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={clients} cx="50%" cy="50%" innerRadius={60} outerRadius={95} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {clients.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => eur(v)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" />Du réalisé au business potentiel</CardTitle>
          <CardDescription>Le réalisé et le signé sont acquis ; devis et business potentiel sont des probabilités, jamais du CA. Source : HubSpot, instantané du {new Date(d.date_instantane).toLocaleDateString("fr-FR")}.</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={funnel}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(222,20%,20%)" />
              <XAxis dataKey="etape" stroke="hsl(220,15%,55%)" fontSize={12} />
              <YAxis stroke="hsl(220,15%,55%)" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => eur(v)} />
              <Legend />
              <Bar dataKey="ferme" name="Acquis (réalisé / signé)" fill="hsl(38,90%,55%)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="potentiel" name="Montant potentiel" fill="hsl(200,70%,50%)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="pondere" name="Potentiel pondéré (probabilité)" fill="hsl(150,60%,45%)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Briefcase className="h-4 w-4 text-primary" />Ateliers IA, 2026 à 2027</CardTitle>
            <CardDescription>Potentiel {eur(atelTotal)} · pondéré {eur(atelPondere)}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {ateliers.map((a) => (
              <div key={a.deal} className="p-3 rounded-lg border border-border bg-secondary/30">
                <p className="text-sm text-foreground">{a.deal}</p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">{eur(a.montant_eur)}</Badge>
                  <Badge variant="outline" className="text-[10px] border-muted-foreground/30 text-muted-foreground">Probabilité {Math.round(a.proba * 100)} %</Badge>
                  {a.cloture_prevue && <Badge variant="outline" className="text-[10px] border-muted-foreground/30 text-muted-foreground">Jusqu'au {new Date(a.cloture_prevue).toLocaleDateString("fr-FR")}</Badge>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Business potentiel, détail HubSpot</CardTitle>
            <CardDescription>{potentiel.length} affaires, par montant décroissant</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1.5 max-h-80 overflow-y-auto">
            {potentiel.map((x) => (
              <div key={x.deal} className="flex items-center justify-between gap-3 text-sm border-b border-border/40 py-1.5">
                <div className="min-w-0">
                  <p className="truncate text-foreground">{x.deal}</p>
                  <p className="text-[10px] text-muted-foreground">{x.nature} · {Math.round(x.proba * 100)} %</p>
                </div>
                <span className="shrink-0 font-medium">{eur(x.montant_eur)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-yellow-400" />Points d'attention</CardTitle>
            <CardDescription>Calculés sur vos chiffres réels</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.map((a, i) => (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${alertStyle(a.type)}`}>
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <p className="text-sm">{a.message}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><UserCheck className="h-4 w-4 text-primary" />Devis à relancer en priorité</CardTitle>
            <CardDescription>Classés par montant pondéré par la probabilité</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {actions.map((a) => (
              <div key={a.deal} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-secondary/30">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{a.deal}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">{eur(a.montant_eur)}</Badge>
                    <Badge variant="outline" className="text-[10px] border-muted-foreground/30 text-muted-foreground">Probabilité {Math.round(a.proba * 100)} %</Badge>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
